import type { Profile } from "@/domain/types";

// ---- Default: real Deadlock PC defaults (mid-2026). Confidence per the 2026-06-16 research sweep:
// Fire/Zoom/Reload/Melee/Parry/Jump/Dash/Crouch/Scoreboard/Ping/Chat/PTT = high (deadlock.wiki,
// shacknews controls table, playdeadlock forums). Teammate cams = F1-F5 (NOT 1-5: those are ability
// keys). Zipline copies Jump (Space) by default. Alternate cast has NO shipped default (Quick Cast is
// default; the Alt-Cast Button is opt-in, user-assigned) so it is omitted, not guessed. Fly up/down
// are spectator/flight only and low-confidence (a known free-cam label-reversal bug); placed in the
// Replay Context at the researched Shift/Ctrl with that caveat. Camera mode = C (medium).
export const defaultProfile: Profile = {
  id: "default",
  name: "Default",
  base: [[]], // no shared base: Match and Replay are disjoint Contexts
  contexts: {
    match: [
      // combat
      { binding: { name: "Fire", category: "combat" }, token: "MouseLeft" },
      { binding: { name: "Zoom", category: "combat", note: "Alt-fire" }, token: "MouseRight" },
      { binding: { name: "Reload", category: "combat" }, token: "R" },
      { binding: { name: "Melee", category: "combat", note: "Tap light; hold heavy" }, token: "Q" },
      { binding: { name: "Parry", category: "combat", note: "Throw held item" }, token: "F" },
      // ability (Alternate cast omitted: no shipped default, see header)
      { binding: { name: "Ability 1", category: "ability" }, token: "1" },
      { binding: { name: "Ability 2", category: "ability" }, token: "2" },
      { binding: { name: "Ability 3", category: "ability" }, token: "3" },
      { binding: { name: "Ultimate", category: "ability", note: "Ability 4" }, token: "4" },
      // item
      { binding: { name: "Item 1", category: "item" }, token: "Z" },
      { binding: { name: "Item 2", category: "item" }, token: "X" },
      { binding: { name: "Item 3", category: "item" }, token: "C" },
      { binding: { name: "Item 4", category: "item" }, token: "V" },
      {
        binding: { name: "Quick Buy", category: "item", note: "Was Shift+B before May 2025" },
        token: "G",
      },
      // movement
      { binding: { name: "Forward", category: "movement" }, token: "W" },
      { binding: { name: "Left", category: "movement" }, token: "A" },
      { binding: { name: "Back", category: "movement" }, token: "S" },
      { binding: { name: "Right", category: "movement" }, token: "D" },
      {
        binding: {
          name: "Jump",
          category: "movement",
          note: "Jump / mantle; zipline / rope shares this key by default",
        },
        token: "Space",
      },
      {
        binding: {
          name: "Dash",
          category: "movement",
          note: "Fly up shares this key during hero flight",
        },
        token: "Shift",
      },
      {
        binding: {
          name: "Crouch",
          category: "movement",
          note: "Slide while sprinting; fly down shares this key during hero flight",
        },
        token: "Ctrl",
      },
      // social / comms
      {
        binding: { name: "Ping", category: "social", note: "Tap to ping; hold for wheel" },
        token: "MouseMiddle",
      },
      {
        binding: { name: "Chat", category: "social", note: "Team chat (Shift+Enter for all)" },
        token: "Enter",
      },
      { binding: { name: "Push to Talk", category: "social", note: "Voice (team)" }, token: "T" },
      {
        binding: { name: "Minimap Ping", category: "social", note: "Hold, click the minimap" },
        token: "Alt",
      },
      // interface (menu / HUD) - same category + colour as Destiny's Interface
      { binding: { name: "Open Shop", category: "interface" }, token: "B" },
      {
        binding: {
          name: "HUD detail view",
          category: "interface",
          note: "Hold; expands the HUD / scoreboard",
        },
        token: "Tab",
      },
      // camera (in-match teammate cameras; default F1-F5, not the ability digits). The cap shows
      // the short "Cam N"; the readout note carries the full "Teammate N camera" (long names
      // shrink to an unreadable floor on a 1u cap, so cameras follow SC2's existing "Cam N" naming).
      { binding: { name: "Cam 1", category: "camera", note: "Teammate 1 camera" }, token: "F1" },
      { binding: { name: "Cam 2", category: "camera", note: "Teammate 2 camera" }, token: "F2" },
      { binding: { name: "Cam 3", category: "camera", note: "Teammate 3 camera" }, token: "F3" },
      { binding: { name: "Cam 4", category: "camera", note: "Teammate 4 camera" }, token: "F4" },
      { binding: { name: "Cam 5", category: "camera", note: "Teammate 5 camera" }, token: "F5" },
    ],
    replay: [
      { binding: { name: "Switch teams", category: "spectator" }, token: "Space" },
      {
        binding: { name: "Camera mode", category: "spectator", note: "Toggle free-cam" },
        token: "C",
      },
      {
        binding: {
          name: "Fly up",
          category: "spectator",
          note: "Free-cam ascend (default uncertain: free-cam labels are shown reversed in-game)",
        },
        token: "Shift",
      },
      {
        binding: {
          name: "Fly down",
          category: "spectator",
          note: "Free-cam descend (default uncertain: free-cam labels are shown reversed in-game)",
        },
        token: "Ctrl",
      },
      {
        binding: { name: "Spectate in-eye", category: "spectator", note: "First-person POV" },
        token: "WheelUp",
      },
      { binding: { name: "Spectate chase cam", category: "spectator" }, token: "WheelDown" },
      { binding: { name: "Toggle free cursor", category: "spectator" }, token: "J" },
      { binding: { name: "Dual-team minimap view", category: "spectator" }, token: "F1" },
      { binding: { name: "Target's minimap view", category: "spectator" }, token: "F2" },
      { binding: { name: "Amber minimap view", category: "spectator" }, token: "F3" },
      { binding: { name: "Sapphire minimap view", category: "spectator" }, token: "F4" },
      { binding: { name: "Increase replay speed", category: "spectator" }, token: "=" },
      { binding: { name: "Decrease replay speed", category: "spectator" }, token: "-" },
      { binding: { name: "Replay pause", category: "spectator" }, token: "P" },
    ],
  },
};
